import type { GiftWizardProps } from '@consumer/modules/checkout/GiftWizardModal.vue'
import { useOrderStore, type DeliveryData } from '@consumer/services/order'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { Contact } from '@consumer/types/serializers'
import { openGiftWizard } from '@consumer/services/modals'
import { validateTagline as validateTaglineApi } from '@consumer/logic/giftly'
import { translateErrorToString } from '@shared/validation'
import { AxiosError } from 'axios'
import { contacts } from '@consumer/api'
import { currentParams } from '@shared/url'
import { isBeforeToday, isDateStr, isToday } from '@consumer/utils/date'

// Prefill the Delivery step from the recipient's saved contact when arriving
// via a `recipient_contact_id` link (event page / contact gift ideas). The
// link names an explicit recipient, so it replaces whatever an abandoned
// draft carried (a previous event's prefill or typed values); a failed lookup
// (logged out, someone else's contact) leaves the draft untouched.
//
// Owner-entered fields prefill as plain values (as if typed); when the contact
// only has verified info from their connected account, the delivery is linked
// to the contact with the "user" address source instead — the value shown is
// masked and the server resolves the real address at purchase.
async function prefillDeliveryRecipient (delivery: DeliveryData) {
  const contactId = currentParams().get('recipient_contact_id')
  if (!contactId) return

  const contact = await contacts.show<Contact>({ id: contactId }).catch(() => null)
  if (!contact) return

  // Re-entering from the same contact's link (refresh, back) keeps the draft —
  // including any tweaks made in the wizard; only a different recipient resets.
  if (draftMatchesContact(delivery, contact)) return

  resetDraftRecipient(delivery)

  if (contact.email) {
    delivery.deliveryMethod = 'email'
    delivery.recipientEmail = contact.email
  }
  else if (contact.phoneNumber) {
    delivery.deliveryMethod = 'text'
    delivery.recipientPhoneNumber = contact.phoneNumber
  }
  else if (contact.connectedUserEmail) {
    linkVerifiedRecipient(delivery, contact)
    delivery.deliveryMethod = 'email'
    delivery.recipientEmail = contact.connectedUserEmail
  }
  else if (contact.connectedUserPhoneNumber) {
    linkVerifiedRecipient(delivery, contact)
    delivery.deliveryMethod = 'text'
    delivery.recipientPhoneNumber = contact.connectedUserPhoneNumber
  }
}

function draftMatchesContact (delivery: DeliveryData, contact: Contact) {
  if (delivery.recipientId && delivery.recipientId === String(contact.id)) return true

  const emails = [contact.email, contact.connectedUserEmail].filter(Boolean)
  if (delivery.recipientEmail && emails.includes(delivery.recipientEmail)) return true

  const phones = [contact.phoneNumber, contact.connectedUserPhoneNumber].filter(Boolean)
  return Boolean(delivery.recipientPhoneNumber && phones.includes(delivery.recipientPhoneNumber))
}

// Clears the delivery date too: the new link's own delivery_date (if any) is
// applied right after the prefill, so a stale draft can't pair the previous
// event's date with the new recipient.
function resetDraftRecipient (delivery: DeliveryData) {
  delivery.recipient = null
  delivery.recipientType = null
  delivery.recipientId = null
  delivery.recipientAddressSource = undefined
  delivery.recipientEmail = undefined
  delivery.recipientPhoneNumber = undefined
  delivery.recipientMailingAddress = undefined
  delivery.sameEmailReason = undefined
  delivery.deliveryDate = null
  delivery.deliveryTime = null
  delivery.deliverAt = null
}

function linkVerifiedRecipient (delivery: DeliveryData, contact: Contact) {
  delivery.recipient = contact
  delivery.recipientType = 'Consumer::Contact'
  delivery.recipientId = String(contact.id)
  delivery.recipientAddressSource = 'user'
}

export async function checkoutGift (
  gift: GiftTemplate,
  props?: Omit<GiftWizardProps, 'orderGift'>,
  {
    validateTagline = false,
    onTaglineError = undefined,
  }: {
    validateTagline?: boolean
    onTaglineError?: (error: string) => void
  } = {},
) {
  if (validateTagline && gift.giftlyMetadata?.tagline) {
    try {
      await validateTaglineApi(gift.giftlyMetadata?.tagline, gift)
    }
    catch (error) {
      if (error instanceof AxiosError && error.status === 422 && onTaglineError) {
        onTaglineError(translateErrorToString(error))
        return
      }
      else {
        throw error
      }
    }
  }

  const { orderGiftDraft: orderGift, prefillGifterEmail } = useOrderStore()

  const { id, ...attrs } = gift // Ensure we never reassign the id.
  Object.assign(orderGift, attrs)

  if (currentParams().get('recipient_contact_id')) {
    orderGift.delivery ||= {}
    await prefillDeliveryRecipient(orderGift.delivery)
  }

  const urlDeliveryDate = currentParams().get('delivery_date')
  if (urlDeliveryDate && isDateStr(urlDeliveryDate)
    && !isToday(urlDeliveryDate) && !isBeforeToday(urlDeliveryDate)
    && !orderGift.delivery?.deliveryDate) {
    orderGift.delivery ||= {}
    orderGift.delivery.deliveryDate = urlDeliveryDate
  }

  // The buyer's email: an explicit caller value wins, else the reminder link's
  // URL param (logged-out flow). Resolve once and use it for both the store
  // prefill and the wizard prop so they can't disagree.
  const emailFromParams = props?.emailFromParams || currentParams().get('gifter_email') || undefined
  // Pre-complete Contact from the known email (like the logged-in flow) so the
  // wizard opens on Delivery instead of asking the guest to re-confirm it.
  if (emailFromParams) prefillGifterEmail(emailFromParams)

  return openGiftWizard({ orderGift, ...props, emailFromParams })
}
